import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as THREE from 'three'
import './LandingPage.css'

const VERTEX_SHADER = `
uniform float uTime;
uniform float uDistortion;
uniform float uSize;
uniform vec2 uMouse;

varying float vAlpha;
varying vec3 vPos;
varying float vNoise;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
    const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
    const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i  = floor(v + dot(v, C.yyy) );
    vec3 x0 = v - i + dot(i, C.xxx) ;
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min( g.xyz, l.zxy );
    vec3 i2 = max( g.xyz, l.zxy );
    vec3 x1 = x0 - i1 + 1.0 * C.xxx;
    vec3 x2 = x0 - i2 + 2.0 * C.xxx;
    vec3 x3 = x0 - 1.0 + 3.0 * C.xxx;
    i = mod289(i);
    vec4 p = permute( permute( permute(
                i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
    float n_ = 1.0/7.0;
    vec3  ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z *ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_ );
    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4( x.xy, y.xy );
    vec4 b1 = vec4( x.zw, y.zw );
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
    vec3 p0 = vec3(a0.xy,h.x);
    vec3 p1 = vec3(a0.zw,h.y);
    vec3 p2 = vec3(a1.xy,h.z);
    vec3 p3 = vec3(a1.zw,h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3) ) );
}

void main() {
    vec3 pos = position;

    float noiseFreq = 0.8;
    float noiseAmp = uDistortion;
    float noise = snoise(vec3(pos.x * noiseFreq + uTime * 0.2, pos.y * noiseFreq, pos.z * noiseFreq));

    vNoise = noise;

    vec3 newPos = pos + (normalize(pos) * noise * noiseAmp);

    float dist = distance(uMouse * 10.0, newPos.xy);
    float interaction = smoothstep(5.0, 0.0, dist);
    newPos += normalize(pos) * interaction * 0.8;

    vec4 mvPosition = modelViewMatrix * vec4(newPos, 1.0);
    gl_Position = projectionMatrix * mvPosition;

    gl_PointSize = uSize * (20.0 / -mvPosition.z) * (1.0 + noise * 0.2);

    vAlpha = 1.0;
    vPos = newPos;
}
`

const FRAGMENT_SHADER = `
uniform vec3 uColor;
uniform float uOpacity;

varying float vNoise;
varying vec3 vPos;

void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);

    if (dist > 0.5) discard;

    float alpha = smoothstep(0.5, 0.1, dist) * uOpacity;

    vec3 darkColor = uColor * 0.3;
    vec3 lightColor = uColor * 2.5;

    vec3 finalColor = mix(darkColor, lightColor, vNoise * 0.6 + 0.4);

    gl_FragColor = vec4(finalColor, alpha);
}
`

const ICON_PATHS = {
  'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
  radio: (
    <>
      <circle cx="12" cy="12" r="2" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 16.25a6 6 0 0 1 0-8.49M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14" />
    </>
  ),
  cpu: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </>
  ),
  layers: (
    <>
      <path d="M12 2 2 7l10 5 10-5-10-5Z" />
      <path d="m2 12 10 5 10-5" />
      <path d="m2 17 10 5 10-5" />
    </>
  ),
  chart: <path d="M3 3v18h18M7 14l4-4 4 4 5-6" />,
  sliders: <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />,
  server: (
    <>
      <rect x="2" y="2" width="20" height="8" rx="2" />
      <rect x="2" y="14" width="20" height="8" rx="2" />
      <path d="M6 6h.01M6 18h.01" />
    </>
  ),
  star: <path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6L12 16.9l-5.4 2.8 1-6-4.4-4.3 6.1-.9L12 3z" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </>
  ),
  rss: (
    <>
      <path d="M4 11a9 9 0 0 1 9 9M4 4a16 16 0 0 1 16 16" />
      <circle cx="5" cy="19" r="1" />
    </>
  ),
  newspaper: (
    <>
      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-4 0v-9c0-1.1.9-2 2-2h2" />
      <path d="M18 14h-8M15 18h-5M10 6h8v4h-8V6Z" />
    </>
  ),
  zap: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />,
  hash: <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  bot: (
    <>
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M12 8V4M8 4h8" />
      <circle cx="9" cy="14" r="1" />
      <circle cx="15" cy="14" r="1" />
    </>
  ),
}

function Icon({ name, size = 24, className = '', style }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  )
}

const COVERAGE_SOURCES = [
  { name: 'ANI', icon: 'zap' },
  { name: 'PTI', icon: 'rss' },
  { name: 'Reuters', icon: 'globe' },
  { name: 'AFP', icon: 'newspaper' },
  { name: 'BBC', icon: 'radio' },
  { name: 'CNN', icon: 'hash' },
  { name: 'NDTV', icon: 'globe' },
  { name: 'Hindustan Times', icon: 'newspaper' },
  { name: 'The Hindu', icon: 'newspaper' },
  { name: 'India Today', icon: 'zap' },
  { name: 'Al Jazeera', icon: 'globe' },
  { name: 'Times Now', icon: 'hash' },
]

export default function LandingPage() {
  const navigate = useNavigate()
  const canvasRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [accessEmail, setAccessEmail] = useState('')

  useEffect(() => {
    document.body.classList.add('lp-landing')
    return () => document.body.classList.remove('lp-landing')
  }, [])

  useEffect(() => {
    const container = canvasRef.current
    if (!container) return undefined

    const scene = new THREE.Scene()
    scene.fog = new THREE.FogExp2(0x09090b, 0.035)

    const camera = new THREE.PerspectiveCamera(
      50,
      container.clientWidth / container.clientHeight,
      0.1,
      100
    )
    camera.position.set(0, 0, 18)

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setSize(container.clientWidth, container.clientHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    container.appendChild(renderer.domElement)

    const systemsGroup = new THREE.Group()
    systemsGroup.position.x = 4.0
    scene.add(systemsGroup)

    const isSmallScreen = window.innerWidth < 1024
    const geometry = new THREE.IcosahedronGeometry(4.0, isSmallScreen ? 24 : 35)

    const uniforms = {
      uTime: { value: 0 },
      uDistortion: { value: 0.6 },
      uSize: { value: 1.8 },
      uColor: { value: new THREE.Color('#f97316') },
      uOpacity: { value: 0.8 },
      uMouse: { value: new THREE.Vector2(0, 0) },
    }

    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })

    const particles = new THREE.Points(geometry, material)
    systemsGroup.add(particles)

    const lineGroup = new THREE.Group()
    systemsGroup.add(lineGroup)

    const createTechOrbit = (radius, rotation) => {
      const curve = new THREE.EllipseCurve(0, 0, radius, radius, 0, 2 * Math.PI, false, 0)
      const points = curve.getPoints(128)
      const geo = new THREE.BufferGeometry().setFromPoints(points)
      const mat = new THREE.LineBasicMaterial({ color: 0x3f3f46, transparent: true, opacity: 0.5 })
      const orbit = new THREE.Line(geo, mat)
      orbit.rotation.x = rotation.x
      orbit.rotation.y = rotation.y
      lineGroup.add(orbit)
      return orbit
    }

    const orbits = [
      createTechOrbit(5.5, { x: Math.PI / 2, y: 0 }),
      createTechOrbit(5.2, { x: Math.PI / 3, y: Math.PI / 6 }),
      createTechOrbit(6.0, { x: Math.PI / 1.8, y: Math.PI / 4 }),
    ]

    let time = 0
    let mouseX = 0
    let mouseY = 0
    let frameId

    const onMouseMove = (e) => {
      mouseX = (e.clientX / window.innerWidth) * 2 - 1
      mouseY = -(e.clientY / window.innerHeight) * 2 + 1
      uniforms.uMouse.value.x += (mouseX - uniforms.uMouse.value.x) * 0.05
      uniforms.uMouse.value.y += (mouseY - uniforms.uMouse.value.y) * 0.05
    }

    const adjustLayout = () => {
      if (window.innerWidth < 1024) {
        systemsGroup.position.set(0, 1.5, -5)
        systemsGroup.scale.set(0.8, 0.8, 0.8)
      } else {
        systemsGroup.position.set(4.5, 0, 0)
        systemsGroup.scale.set(1, 1, 1)
      }
    }

    const onResize = () => {
      camera.aspect = container.clientWidth / container.clientHeight
      camera.updateProjectionMatrix()
      renderer.setSize(container.clientWidth, container.clientHeight)
      adjustLayout()
    }

    const animate = () => {
      frameId = requestAnimationFrame(animate)
      time += 0.015

      systemsGroup.rotation.y = time * 0.08
      systemsGroup.rotation.z = Math.sin(time * 0.1) * 0.05

      lineGroup.rotation.x = Math.sin(time * 0.05) * 0.2
      orbits.forEach((orbit, i) => {
        orbit.rotation.z += 0.003 * (i + 1)
      })

      camera.position.x += (mouseX * 0.5 - camera.position.x) * 0.05
      camera.position.y += (mouseY * 0.5 - camera.position.y) * 0.05
      camera.lookAt(0, 0, 0)

      uniforms.uTime.value = time
      renderer.render(scene, camera)
    }

    document.addEventListener('mousemove', onMouseMove)
    window.addEventListener('resize', onResize)
    adjustLayout()
    animate()

    return () => {
      cancelAnimationFrame(frameId)
      document.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('resize', onResize)
      geometry.dispose()
      material.dispose()
      orbits.forEach((orbit) => orbit.geometry.dispose())
      renderer.dispose()
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement)
      }
    }
  }, [])

  const goToLogin = () => navigate('/login')

  return (
    <div className="lp-page">
      {/* ================= HERO ================= */}
      <section className="lp-hero">
        <div className="lp-hero-grid lp-grid-overlay" />
        <div className="lp-hero-canvas" ref={canvasRef} />
        <div className="lp-hero-orb" />

        <header className="lp-header lp-fade-in">
          <div className="lp-brand">
            <div className="lp-brand-row">
              <div className="lp-logo">FS</div>
              <span className="lp-brand-name">First Signal</span>
            </div>
            <div className="lp-brand-sub">
              <span>Realtime News Intelligence</span>
              <i />
            </div>
          </div>

          <nav className="lp-nav">
            <div className="lp-nav-links">
              <a href="#capabilities" className="lp-nav-link">Capabilities</a>
              <a href="#coverage" className="lp-nav-link">Coverage</a>
              <button type="button" className="lp-login-btn" onClick={goToLogin}>
                Login
              </button>
            </div>

            <button
              type="button"
              className="lp-login-btn lp-login-mobile"
              onClick={goToLogin}
              aria-label="Login"
            >
              Login
            </button>

            <button
              type="button"
              className="lp-menu-btn"
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <Icon name={menuOpen ? 'x' : 'hash'} size={20} />
            </button>

            {menuOpen && (
              <div className="lp-mobile-menu lp-glass">
                <a href="#capabilities" className="lp-mobile-link" onClick={() => setMenuOpen(false)}>
                  Capabilities
                </a>
                <a href="#coverage" className="lp-mobile-link" onClick={() => setMenuOpen(false)}>
                  Coverage
                </a>
                <a href="#access" className="lp-mobile-link" onClick={() => setMenuOpen(false)}>
                  Request Access
                </a>
              </div>
            )}
          </nav>
        </header>

        <div className="lp-hero-content lp-fade-in">
          <div className="lp-eyebrow-row">
            <span className="lp-eyebrow-line" />
            <span className="lp-eyebrow-text">Operational Intelligence</span>
          </div>

          <h1 className="lp-h1">
            News breaks.
            <br />
            <span className="lp-h1-dim">You&apos;re first.</span>
          </h1>

          <p className="lp-hero-sub">
            Our AI understands your news channel&apos;s needs and finds out and alerts you for the
            most important news instantly.
          </p>

          <p className="lp-hero-note">
            Small accounts are the ones which uncover news in real time on X — our AI tracks
            exactly those to get you relevant news fastest.
          </p>

          <div className="lp-cta-row">
            <button type="button" className="lp-btn-primary" onClick={goToLogin}>
              Open the Console
              <Icon name="arrow-right" size={16} />
            </button>
            <div className="lp-status">
              <span className="lp-status-dot" />
              <span className="lp-status-text">Feed Online</span>
            </div>
          </div>
        </div>

        <div className="lp-protocol">
          <span>Protocol v4.0.1</span>
          <span>Encrypted Tunnel</span>
        </div>
      </section>

      {/* ================= CAPABILITIES ================= */}
      <section className="lp-section" id="capabilities">
        <div className="lp-container">
          <div className="lp-section-head">
            <h2 className="lp-h2">What You Get</h2>
            <p className="lp-section-p">
              End-to-end news monitoring across X — from raw tweet ingestion to structured,
              ready-to-file briefs.
            </p>
          </div>

          <div className="lp-cards">
            <div className="lp-card">
              <div className="lp-card-icon">
                <Icon name="radio" size={22} />
              </div>
              <h3>Live Signal Stream</h3>
              <p>
                WebSocket streaming pushes matched tweets to your console the moment they&apos;re
                posted — no refresh, no lag, no missed beats.
              </p>
            </div>

            <div className="lp-card">
              <div className="lp-card-icon">
                <Icon name="cpu" size={22} />
              </div>
              <h3>AI Briefing Engine</h3>
              <p>
                Every tracked tweet is distilled into a structured summary with one-line headlines
                and locations, so you scan hours of chatter in seconds.
              </p>
            </div>

            <div className="lp-card">
              <div className="lp-card-icon">
                <Icon name="layers" size={22} />
              </div>
              <h3>Multi-Account Tracking</h3>
              <p>
                Follow up to 80 handles per desk — agencies, beat reporters, breaking-news accounts
                — all deduplicated and filtered for actual news.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CASE STUDY ================= */}
      <section className="lp-case">
        <div className="lp-case-inner">
          <div className="lp-case-media">
            <div className="lp-case-img-wrap">
              <img
                src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=1200&q=80"
                alt="Stack of newspapers representing breaking news coverage"
                loading="lazy"
              />
              <div className="lp-case-img-fade" />

              <div className="lp-case-badges">
                <div className="lp-badge">
                  <span>Ingest Latency</span>
                  <b>&lt;60s</b>
                </div>
                <div className="lp-badge lp-live-badge">
                  <i />
                  <span>Live Sync</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lp-case-copy">
            <div className="lp-tagline-row">
              <Icon name="chart" size={20} />
              <span>Why It Exists</span>
            </div>

            <h2 className="lp-case-h2">
              Built for journalists who are tired of finding stories last.
            </h2>

            <p className="lp-case-p">
              Breaking news surfaces on X long before it reaches wires or TV. FirstSignal sits
              between you and that firehose — polling agency accounts on a smart schedule,
              filtering out noise with AI, and broadcasting only genuine news straight to your
              feed in realtime.
            </p>

            <div className="lp-stats">
              <div>
                <div className="lp-stat-num">80+</div>
                <div className="lp-stat-label">Feeds Per Desk</div>
              </div>
              <div>
                <div className="lp-stat-num">&lt;60s</div>
                <div className="lp-stat-label">Tweet To Brief</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= COVERAGE ================= */}
      <section className="lp-tiles-section" id="coverage">
        <div className="lp-center-head">
          <h2 className="lp-h2">Coverage is baked in.</h2>
          <p className="lp-text-balance">
            Track agencies, wire services and beat reporters from one console. Add any public
            account in seconds — no lock-in.
          </p>
        </div>

        <div className="lp-tiles">
          {COVERAGE_SOURCES.map((src) => (
            <div className="lp-tile" key={src.name}>
              <div className="lp-tile-icon">
                <Icon name={src.icon} size={24} />
              </div>
              <span className="lp-tile-name">{src.name}</span>
              <div className="lp-tile-cta">
                <span>Monitored</span>
                <Icon name="arrow-right" size={12} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= ENGINE CTA ================= */}
      <section className="lp-engine">
        <div className="lp-engine-bg">
          <div className="lp-grid-overlay" />

          <div className="lp-type-sim" aria-hidden="true">
            <div>Add Feeds Add Feeds Add Feeds</div>
            <div>Poll · Summarize · Broadcast</div>
            <div>Search accounts... Search accounts...</div>
          </div>

          <div className="lp-agent-chip">
            <span>
              <Icon name="bot" size={20} /> AI Briefings
            </span>
          </div>

          <div className="lp-engine-fade" />
        </div>

        <div className="lp-engine-inner">
          <div className="lp-engine-copy">
            <div className="lp-mini-badge">
              <span>The Pipeline</span>
            </div>

            <h2 className="lp-engine-h2">
              From raw tweets to ready-to-file briefs — automatically.
            </h2>

            <p className="lp-engine-p">
              Smart polling respects per-account cadence and API limits. AI summarization filters
              boilerplate from genuine news. WebSocket broadcast delivers every confirmed item
              instantly.
            </p>

            <button type="button" className="lp-btn-blue" onClick={goToLogin}>
              Start Monitoring
              <Icon name="arrow-right" size={16} />
            </button>
          </div>

          <div className="lp-statbox-col">
            <div className="lp-statbox lp-glass">
              <h3>A realtime pipeline tuned for newsroom velocity</h3>

              <div className="lp-statbox-card">
                <span>Dedup &amp; Noise Filtering</span>
                <div className="lp-statbox-num">24/7</div>
                <p>
                  Continuous polling with durable state, per-account intervals, and automatic
                  retry — your feed never sleeps even when you do.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= ACCESS CTA ================= */}
      <section className="lp-access" id="access">
        <div className="lp-access-overlay lp-grid-overlay lp-tex" />
        <div className="lp-access-orb" />

        <div className="lp-access-inner">
          <div className="lp-star-row">
            <Icon name="star" size={20} />
            <span>Early Access</span>
          </div>

          <h2 className="lp-access-h2 lp-text-balance">
            Ready to catch the <span className="lp-gold-text">first signal?</span>
          </h2>

          <p className="lp-access-p lp-text-balance">
            Access is provisioned by your administrator. Leave your work email and we&apos;ll get
            you set up.
          </p>

          <form
            className="lp-access-form"
            onSubmit={(e) => {
              e.preventDefault()
              const subject = encodeURIComponent('Early Access Request — First Signal')
              const body = encodeURIComponent(
                [
                  'Hi Pratyush,',
                  '',
                  'I would like to request early access to First Signal.',
                  '',
                  'Details:',
                  `- Work email: ${accessEmail.trim() || '(not provided)'}`,
                  '- Organization / desk: ',
                  '- Accounts I want to track: ',
                  '',
                  'Thanks,',
                ].join('\n')
              )
              window.location.href = `mailto:gargpratyush999@gmail.com?subject=${subject}&body=${body}`
            }}
          >
            <input
              type="email"
              className="lp-access-input"
              placeholder="Enter your work email"
              aria-label="Work email"
              value={accessEmail}
              onChange={(e) => setAccessEmail(e.target.value)}
            />
            <button type="submit" className="lp-btn-gold">
              Request Access
              <Icon name="arrow-right" size={16} />
            </button>
          </form>

          <p className="lp-access-note">Already have an account? Use the Login button above.</p>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="lp-footer">
        <div className="lp-footer-grid">
          <div className="lp-footer-brand">
            <div className="lp-footer-logo-row">
              <div className="lp-footer-logo">FS</div>
              <span>First Signal</span>
            </div>
            <p className="lp-footer-desc">
              Realtime news intelligence. We monitor the accounts that break stories, so you file
              them first.
            </p>
          </div>

          <div className="lp-footer-col">
            <h4>Product</h4>
            <ul>
              <li><a href="#capabilities">Capabilities</a></li>
              <li><a href="#coverage">Coverage</a></li>
              <li><a href="#access">Access</a></li>
              <li><a href="/login" onClick={(e) => { e.preventDefault(); goToLogin() }}>Console</a></li>
            </ul>
          </div>

          <div className="lp-footer-col">
            <h4>Resources</h4>
            <ul>
              <li><a href="#capabilities">How It Works</a></li>
              <li><a href="#access">Early Access</a></li>
              <li><a href="#coverage">Source List</a></li>
            </ul>
          </div>

          <div className="lp-footer-col">
            <h4>Company</h4>
            <ul>
              <li><a href="#access">Contact</a></li>
              <li><a href="#access">Privacy Policy</a></li>
            </ul>
          </div>
        </div>

        <div className="lp-footer-bar">
          <span className="lp-footer-copy">© 2026 First Signal. All rights reserved.</span>
          <div className="lp-footer-status">
            <i />
            <span>All Systems Operational</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
